/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { ApolloError } from 'apollo-client'
import { Cell, Grid, HFlow, Modal, ModalBody, ModalFooter, ModalHeader } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import useSession from 'components/auth/useSession'
import { useErrorHandler } from 'components/error'
import { FooterButton } from 'components/footer-button'
import {
  CidadaoAtendimentoSelectField,
  Form,
  FormRenderProps,
  LocalAtendimentoSelectField,
  TimeField,
} from 'components/form'
import { RegistroTardioJustificativaSelectField } from 'components/form/field/select/JustificativaRegistroTardioSelectField/RegistroTardioJustificativaSelectField'
import {
  LotacaoAndEstagioSelectField,
  LotacaoAndEstagioSelectFieldModel,
} from 'components/form/field/select/LotacaoAndEstagioSelectField/LotacaoAndEstagioSelectField'
import { DateField } from 'components/form/final-form/DateField/DateField'
import { useFlags } from 'config/useFlagsContext'
import { FormApi } from 'final-form'
import {
  useAtendimentoRegistroTardioQuery,
  useCidadaoQuery,
  useEditarRegistroTardioMutation,
} from 'graphql/hooks.generated'
import { useServerTime } from 'hooks/useServerTime'
import { useMemo } from 'react'
import { useHistory, useParams } from 'react-router'
import { Redirect } from 'react-router-dom'
import Permissions from 'types/Permissions'
import { convertDateAndTimeToInstant, formatHoursMinutes, safeDateAsYyyyMmDd } from 'util/date/formatDate'
import { metaPath } from 'util/metaPath'
import { calculator } from 'view/registro-tardio/calculator-registroTardio'
import { DayDelimiterTooltip } from 'view/registro-tardio/components/DayDelimiterTooltip'
import { convertModelToInput } from 'view/registro-tardio/converter'
import { RegistroTardioFormModel } from 'view/registro-tardio/model-registroTardio'
import { REGISTRO_TARDIO_PATH } from 'view/registro-tardio/RegistroTardioRootView'
import { getMinDate } from 'view/registro-tardio/utils-registroTardio'
import { isLocalAtendimentoComJustificativa, validator } from 'view/registro-tardio/validator-registroTardio'

interface UrlParams {
  atendimentoId: string
}

interface EditarRegistroTardioModalFormProps {
  cidadaoIdFromURL?: string
  onSubmit(dataAtendimento: number, cidadaoId: ID): Promise<Boolean>
}

const path = metaPath<RegistroTardioFormModel>()

export const EditarRegistroTardioModalForm = (props: EditarRegistroTardioModalFormProps) => {
  const { cidadaoIdFromURL, onSubmit } = props
  const { hasAuthorization } = useSession()
  const { SAUDE_INDIGENA_ENABLED } = useFlags()

  const canAtender = hasAuthorization(Permissions.visualizarListaDeAtendimento.registrarAtendimento)

  // TODO [DeLorean]: Aguardando CNES da parte do Sesai para definir como será validado as opções especificas da saúde indígena (#19881)
  const canRegistroIndigena = SAUDE_INDIGENA_ENABLED

  const history = useHistory()
  const { acesso } = useAcessoLotacaoOrEstagio()
  const [editarRegistroTardio] = useEditarRegistroTardioMutation()
  const { atendimentoId } = useParams<UrlParams>()
  const { getServerTimeNow } = useServerTime()
  const currentServerTime = getServerTimeNow()
  const minDate = getMinDate(currentServerTime, canRegistroIndigena)

  const handleRejection = useErrorHandler()
  const alert = useAlert()

  const { data, loading } = useAtendimentoRegistroTardioQuery({ variables: { id: atendimentoId } })
  const { justificativa, iniciadoEm, cidadao, responsavel, localAtendimento } = data.atendimento || {}

  const canEdit: boolean = data?.atendimento?.actions?.editar?.enabled

  const decorator = [calculator]

  const { data: newCidadaoToEdit } = useCidadaoQuery({
    fetchPolicy: 'cache-first',
    variables: { id: cidadaoIdFromURL },
    skip: !cidadaoIdFromURL,
  })

  const initialValues: RegistroTardioFormModel = useMemo(
    () => ({
      atendimentoId,
      cidadao: cidadaoIdFromURL ? newCidadaoToEdit?.cidadao : cidadao,
      lotacao: responsavel as LotacaoAndEstagioSelectFieldModel,
      localAtendimento,
      dataAtendimento: safeDateAsYyyyMmDd(iniciadoEm),
      horaAtendimento: formatHoursMinutes(iniciadoEm),
      justificativa,
    }),
    [
      atendimentoId,
      cidadao,
      iniciadoEm,
      justificativa,
      localAtendimento,
      responsavel,
      cidadaoIdFromURL,
      newCidadaoToEdit,
    ]
  )

  const goBack = () => {
    history.replace(REGISTRO_TARDIO_PATH, '')
  }

  const handleSubmit = async (values: RegistroTardioFormModel, formApi: FormApi) => {
    const dataRegistro = convertDateAndTimeToInstant(values.dataAtendimento, values.horaAtendimento)
    const sucesso = await onSubmit(dataRegistro, values?.cidadao?.id)

    if (sucesso) {
      try {
        const res = await editarRegistroTardio({
          variables: { input: convertModelToInput(values) },
        })
        alert('success', `Registro alterado com sucesso.`)
        setTimeout(formApi.reset)
        goBack()
        return res
      } catch (err) {
        handleRejection(err as ApolloError)
      }
    }
  }

  const renderForm = (formProps: FormRenderProps<RegistroTardioFormModel>) => {
    const { values } = formProps

    const justificativaDisabled =
      !values.localAtendimento || !isLocalAtendimentoComJustificativa(values.localAtendimento.id)

    return (
      <Modal
        open
        onClose={goBack}
        closeOnBackdropClick={false}
        style={css`
          width: 100%;
          max-width: 33rem;
        `}
      >
        <ModalHeader title='Editar item na lista de registro tardio' />
        <ModalBody
          style={css`
            margin-right: 2rem;
          `}
        >
          <Grid alignItems='flex-start' gapVertical={1}>
            <Cell size={11}>
              <CidadaoAtendimentoSelectField
                name={path.cidadao}
                label='Cidadão'
                addCidadaoCallbackUrl={REGISTRO_TARDIO_PATH}
                verificarPresenteNaListaAtendimento={false}
                required
              />
            </Cell>
            {!canAtender && (
              <Cell size={11}>
                <LotacaoAndEstagioSelectField
                  name={path.lotacao}
                  label='Profissional'
                  unidadeSaudeId={acesso?.unidadeSaude.id}
                  canWriteRegistroTardio
                  required
                />
              </Cell>
            )}
            <Cell size={5}>
              <DateField
                name={path.dataAtendimento}
                label='Data do atendimento'
                required
                minDate={minDate}
                maxDate={currentServerTime}
                calendarProps={{
                  renderDay: (day: Date) => (
                    <DayDelimiterTooltip day={day} minDate={minDate} maxDate={currentServerTime} />
                  ),
                }}
              />
            </Cell>
            <Cell size={5}>
              <TimeField name={path.horaAtendimento} label='Hora' placeholder='hh:mm' required />
            </Cell>
            <Cell size={11}>
              <LocalAtendimentoSelectField name={path.localAtendimento} label='Local do atendimento' required />
            </Cell>
            <Cell size={11}>
              <RegistroTardioJustificativaSelectField
                name={path.justificativa}
                label='Justificativa'
                required={!justificativaDisabled}
                disabled={justificativaDisabled}
              />
            </Cell>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <FooterButton title='Cancelar' onClick={goBack} size='medium'>
              Cancelar
            </FooterButton>
            <FooterButton title='Salvar' kind='primary' onClick={formProps.handleSubmit} size='medium'>
              Salvar
            </FooterButton>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  if (!loading && !canEdit) return <Redirect to='/403' />

  return (
    <Form<RegistroTardioFormModel>
      render={renderForm}
      initialValues={initialValues}
      decorators={decorator}
      validate={validator(minDate, canAtender)}
      onSubmit={handleSubmit}
    />
  )
}
